import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Truck } from "@phosphor-icons/react";

import { useAuth } from "@/context/AuthContext";

/**
 * /auth/facebook/callback — Facebook redirects here after the user approves.
 * We read `?code=` and `?state=`, verify state (CSRF), then POST the code
 * to /api/auth/facebook via AuthContext.exchangeFacebookCode.
 */
export default function FacebookCallback() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { exchangeFacebookCode } = useAuth();
  const [status, setStatus] = useState("Signing you in…");
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;

    const code = params.get("code");
    const state = params.get("state");
    const error = params.get("error_description") || params.get("error");
    const saved = sessionStorage.getItem("fb_oauth_state");

    if (error) {
      toast.error(`Facebook: ${error}`);
      setStatus("Redirecting…");
      setTimeout(() => nav("/login", { replace: true }), 800);
      return;
    }
    if (!code) {
      toast.error("Missing Facebook code");
      setTimeout(() => nav("/login", { replace: true }), 800);
      return;
    }
    // CSRF protection: BOTH must exist and match. A missing state on either
    // side is an invalid flow — never proceed.
    if (!state || !saved || state !== saved) {
      toast.error("Invalid state — please try again");
      setTimeout(() => nav("/login", { replace: true }), 800);
      return;
    }

    (async () => {
      try {
        await exchangeFacebookCode(code);
        sessionStorage.removeItem("fb_oauth_state");
        toast.success("Welcome!");
        nav("/app", { replace: true });
      } catch (e) {
        const msg =
          e?.response?.data?.detail || e?.message || "Facebook login failed";
        toast.error(typeof msg === "string" ? msg : "Facebook login failed");
        setTimeout(() => nav("/login", { replace: true }), 1200);
      }
    })();
  }, [exchangeFacebookCode, nav, params]);

  return (
    <div className="min-h-screen grid place-items-center bg-[#0a192f] text-white">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 rounded-2xl bg-emerald-500/15 border border-emerald-400/30 grid place-items-center">
          <Truck size={22} weight="duotone" className="text-emerald-400" />
        </div>
        <div className="w-5 h-5 rounded-full border-2 border-emerald-400 border-t-transparent animate-spin" />
        <p className="text-slate-300 text-sm">{status}</p>
      </div>
      <Toaster position="top-right" richColors closeButton theme="dark" />
    </div>
  );
}
